import { useState, useEffect, useCallback, useRef } from 'react'
import type { UserData, DrinkEntry } from '@/types'
import { loadData, saveData, getDefaultUserData, isBlankData, saveToLocalStorage } from '@/utils/storage'
import type { SaveFailureKind } from '@/utils/storage'
import { calculateBAC } from '@/utils/bac'
import { deriveKey, encrypt, decrypt, generateEncryptionSalt, PBKDF2_ITERATIONS, LEGACY_PBKDF2_ITERATIONS } from '@/utils/crypto'
import type { EncryptedPayload } from '@/utils/crypto'
import moment from 'moment'

const isDev = import.meta.env.DEV

export type LoadMode = 'remote' | 'offline-cache' | 'default' | 'error'

export interface SaveError {
  kind: 'encryption' | SaveFailureKind
  message: string
  at: number
  status?: number
}

interface UseUserResult {
  data: UserData
  isLoading: boolean
  isInitialized: boolean
  error: string | null
  loadMode: LoadMode
  offlineSince: number | null
  saveError: SaveError | null
  needsDecryption: boolean
  isEncrypted: boolean
  updateData: (updates: Partial<UserData>) => void
  addEntry: (entry: DrinkEntry) => void
  updateEntry: (index: number, entry: DrinkEntry) => void
  deleteEntry: (index: number) => void
  saveDrinkTemplate: (drink: DrinkEntry['drink']) => void
  supplyPassword: (password: string) => Promise<boolean>
  clearPassword: () => void
  setPasswordForEncryption: (password: string) => Promise<void>
  restoreEncryptionKey: (password: string) => Promise<void>
  removeEncryption: () => void
  retrySave: () => void
  dismissSaveError: () => void
  getCachedPassword: () => string | null
  cachePassword: (password: string) => void
}

export function useUser(userId: string | undefined): UseUserResult {
  const [data, setData] = useState<UserData>(getDefaultUserData())
  const [isLoading, setIsLoading] = useState(true)
  const [isInitialized, setIsInitialized] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loadMode, setLoadMode] = useState<LoadMode>('remote')
  const [offlineSince, setOfflineSince] = useState<number | null>(null)
  const [saveError, setSaveError] = useState<SaveError | null>(null)
  const [needsDecryption, setNeedsDecryption] = useState(false)

  // Ref to prevent saves during initialization
  const allowSavesRef = useRef(false)
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const previousDataRef = useRef<UserData>(getDefaultUserData())
  const retryTickRef = useRef(0)
  const [retryTick, setRetryTick] = useState(0)

  // Encryption state
  const passwordRef = useRef<string | null>(null)
  const encryptionKeyRef = useRef<CryptoKey | null>(null)
  const encryptionSaltRef = useRef<string | null>(null)
  // Iteration count that produced the current encryptionKeyRef. Must match
  // what the on-disk ciphertext was encrypted at, otherwise decrypt fails.
  const encryptionIterationsRef = useRef<number>(PBKDF2_ITERATIONS)
  const pendingEncryptedDataRef = useRef<UserData | null>(null)

  // Load data on mount or userId change
  useEffect(() => {
    if (!userId) {
      setIsLoading(false)
      return
    }

    async function load() {
      setIsLoading(true)
      setError(null)
      setLoadMode('remote')
      setOfflineSince(null)
      allowSavesRef.current = false
      setNeedsDecryption(false)

      try {
        let rawData: UserData

        if (isDev) {
          // In development, check localStorage first, then fall back to the
          // build-time-injected fixture. __SSDL_DEV_FIXTURE__ is replaced by
          // Vite's `define` with the JSON literal in dev builds and with
          // `null` in production builds, so the 1MB fixture never ships.
          const localData = localStorage.getItem('ssdl_data')
          if (localData) {
            console.log('DEV MODE: Loading from localStorage')
            rawData = JSON.parse(localData)
          } else {
            console.log('DEV MODE: Loading from src/dev-fixtures/dev-data.json')
            rawData = __SSDL_DEV_FIXTURE__ as unknown as UserData
          }
          setLoadMode('remote')
        } else {
          const result = await loadData(userId!)
          rawData = result.data
          console.log(`Loaded data from ${result.source}`)
          if (result.source === 'local-offline') {
            setLoadMode('offline-cache')
            setOfflineSince(result.offlineSince)
          } else if (result.source === 'default') {
            setLoadMode('default')
          } else {
            setLoadMode('remote')
          }
        }

        // Check if data is encrypted and needs decryption
        if (rawData.encrypted && rawData.encryptedData) {
          // Store the raw encrypted data for later decryption
          pendingEncryptedDataRef.current = rawData
          encryptionSaltRef.current = rawData.encryptionSalt || null

          // Set metadata fields so Dashboard can show lock screen etc.
          setData({
            ...getDefaultUserData(),
            passwordHash: rawData.passwordHash,
            passwordSalt: rawData.passwordSalt,
            passwordMode: rawData.passwordMode,
            timeoutMinutes: rawData.timeoutMinutes,
            autoLogoutMinutes: rawData.autoLogoutMinutes,
            backupEmail: rawData.backupEmail,
            encrypted: true,
            encryptionSalt: rawData.encryptionSalt,
          })
          setNeedsDecryption(true)
          setIsLoading(false)
          setIsInitialized(true)
          // Don't allow saves until decrypted
          return
        }

        // Not encrypted — process normally
        processAndSetData(rawData)
      } catch (e) {
        console.error('Failed to load data:', e)
        setError('Failed to load data')
        setLoadMode('error')
      } finally {
        setIsLoading(false)
        setIsInitialized(true)
      }
    }

    load()

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }
    }
  }, [userId])

  /** Sort entries and recalculate BAC, then set state */
  const processAndSetData = useCallback((rawData: UserData) => {
    const sortedEntries = [...(rawData.entries || [])].sort((a, b) =>
      moment(a.finished_at).unix() - moment(b.finished_at).unix()
    )

    const entriesWithBAC = calculateBAC(
      sortedEntries,
      rawData.gender || 'male',
      Number(rawData.weight) || 80
    )

    const loadedData = { ...rawData, weight: Number(rawData.weight) || 80, entries: entriesWithBAC }
    setData(loadedData)
    previousDataRef.current = loadedData
    allowSavesRef.current = true

    console.log(`Loaded ${loadedData.entries.length} entries`)
  }, [])

  /** Supply the password to decrypt pending encrypted data */
  const supplyPassword = useCallback(async (password: string): Promise<boolean> => {
    const pending = pendingEncryptedDataRef.current
    if (!pending || !pending.encryptedData || !pending.encryptionSalt) {
      return false
    }

    try {
      // Match the iteration count the data was actually encrypted at;
      // legacy records (encryptionIterations absent) used 100k.
      const storedIterations = pending.encryptionIterations ?? LEGACY_PBKDF2_ITERATIONS
      const key = await deriveKey(password, pending.encryptionSalt, storedIterations)
      const decryptedJson = await decrypt(pending.encryptedData as EncryptedPayload, key)
      const decryptedFields = JSON.parse(decryptedJson) as {
        weight: number
        gender: 'male' | 'female'
        drinks: UserData['drinks']
        entries: UserData['entries']
      }

      // Cache encryption credentials
      passwordRef.current = password
      encryptionKeyRef.current = key
      encryptionSaltRef.current = pending.encryptionSalt
      encryptionIterationsRef.current = storedIterations

      // Merge decrypted fields with metadata
      const fullData: UserData = {
        ...decryptedFields,
        passwordHash: pending.passwordHash,
        passwordSalt: pending.passwordSalt,
        passwordMode: pending.passwordMode,
        timeoutMinutes: pending.timeoutMinutes,
        autoLogoutMinutes: pending.autoLogoutMinutes,
        backupEmail: pending.backupEmail,
        encrypted: true,
        encryptionSalt: pending.encryptionSalt,
      }

      pendingEncryptedDataRef.current = null
      setNeedsDecryption(false)
      processAndSetData(fullData)

      // Save decrypted data to localStorage for convenience
      saveToLocalStorage(fullData)

      return true
    } catch (e) {
      console.error('Decryption failed:', e)
      return false
    }
  }, [processAndSetData])

  /** Clear cached password/key (on lock or logout) */
  const clearPassword = useCallback(() => {
    passwordRef.current = null
    encryptionKeyRef.current = null
  }, [])

  /** Set up encryption for the first time or after password change */
  const setPasswordForEncryption = useCallback(async (password: string) => {
    const salt = generateEncryptionSalt()
    // New encryptions always use the current strong iteration count.
    const key = await deriveKey(password, salt, PBKDF2_ITERATIONS)

    passwordRef.current = password
    encryptionKeyRef.current = key
    encryptionSaltRef.current = salt
    encryptionIterationsRef.current = PBKDF2_ITERATIONS
  }, [])

  /** Re-derive encryption key from password using existing salt (for re-lock/unlock cycles) */
  const restoreEncryptionKey = useCallback(async (password: string) => {
    const salt = encryptionSaltRef.current
    if (!salt) return
    // Use the iteration count tracked from the original derivation so the
    // re-derived key matches the on-disk ciphertext.
    const key = await deriveKey(password, salt, encryptionIterationsRef.current)
    passwordRef.current = password
    encryptionKeyRef.current = key
  }, [])

  /** Remove encryption (when password is removed) */
  const removeEncryption = useCallback(() => {
    passwordRef.current = null
    encryptionKeyRef.current = null
    encryptionSaltRef.current = null
    encryptionIterationsRef.current = PBKDF2_ITERATIONS
  }, [])

  // Save data when it changes (debounced)
  useEffect(() => {
    if (!userId || !allowSavesRef.current || !isInitialized) return

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
    }

    // Validate before scheduling save
    if (isBlankData(data) && !isBlankData(previousDataRef.current)) {
      console.error('BLOCKED: Attempted to save blank data over existing data')
      return
    }

    // Debounce save by 500ms
    saveTimeoutRef.current = setTimeout(async () => {
      // Determine what to actually save
      let dataToSave = data

      // If we have an encryption key, encrypt before saving to remote
      if (encryptionKeyRef.current && encryptionSaltRef.current) {
        try {
          const sensitiveData = JSON.stringify({
            weight: data.weight,
            gender: data.gender,
            drinks: data.drinks,
            entries: data.entries,
          })

          const encryptedPayload = await encrypt(sensitiveData, encryptionKeyRef.current)

          dataToSave = {
            // Metadata stays unencrypted
            weight: 0,
            gender: 'male',
            drinks: [],
            entries: [],
            passwordHash: data.passwordHash,
            passwordSalt: data.passwordSalt,
            passwordMode: data.passwordMode,
            timeoutMinutes: data.timeoutMinutes,
            autoLogoutMinutes: data.autoLogoutMinutes,
            backupEmail: data.backupEmail,
            // Encryption fields
            encrypted: true,
            encryptionSalt: encryptionSaltRef.current,
            encryptionIterations: encryptionIterationsRef.current,
            encryptedData: encryptedPayload,
          }
        } catch (e) {
          const err: SaveError = {
            kind: 'encryption',
            message: (e as Error)?.message || 'Encryption failed',
            at: Date.now(),
          }
          console.error('[ssdl:save] encryption failed', { kind: 'encryption', at: new Date(err.at).toISOString(), error: e })
          setSaveError(err)
          return // Never send plaintext when encryption is expected
        }
      }

      if (isDev) {
        // In development, only save to localStorage (plaintext for convenience)
        console.log('DEV MODE: Saving to localStorage only')
        saveToLocalStorage(data)
        previousDataRef.current = data
        setSaveError(null)
      } else {
        // Save plaintext to localStorage, encrypted to API
        saveToLocalStorage(data)
        const result = await saveData(userId, dataToSave, previousDataRef.current)
        if (!result.success) {
          const err: SaveError = {
            kind: result.kind || 'unknown',
            message: result.error || 'Save failed',
            at: Date.now(),
            status: result.status,
          }
          console.error('[ssdl:save] remote failed', { kind: err.kind, status: err.status, at: new Date(err.at).toISOString(), message: err.message })
          setSaveError(err)
        } else {
          previousDataRef.current = data
          setSaveError(null)
        }
      }
    }, 500)
  }, [data, userId, isInitialized, retryTick])

  // Update data with partial updates
  const updateData = useCallback((updates: Partial<UserData>) => {
    setData(prev => {
      const newData = { ...prev, ...updates }

      // If weight or gender changed, recalculate BAC
      if (updates.weight !== undefined || updates.gender !== undefined) {
        const entries = calculateBAC(
          newData.entries,
          newData.gender,
          newData.weight
        )
        return { ...newData, entries }
      }

      return newData
    })
  }, [])

  // Add a new entry
  const addEntry = useCallback((entry: DrinkEntry) => {
    setData(prev => {
      const newEntries = [...prev.entries, entry]

      // Sort entries chronologically
      newEntries.sort((a, b) =>
        moment(a.finished_at).unix() - moment(b.finished_at).unix()
      )

      // Recalculate BAC
      const entriesWithBAC = calculateBAC(newEntries, prev.gender, prev.weight)

      // Also save drink template if it's not a gap
      let newDrinks = prev.drinks
      if (!entry.is_gap) {
        const existingIndex = prev.drinks.findIndex(d => d.drink === entry.drink.drink)
        if (existingIndex >= 0) {
          newDrinks = [...prev.drinks]
          newDrinks[existingIndex] = entry.drink
        } else {
          newDrinks = [...prev.drinks, entry.drink]
        }
      }

      return { ...prev, entries: entriesWithBAC, drinks: newDrinks }
    })
  }, [])

  // Update an existing entry
  const updateEntry = useCallback((index: number, entry: DrinkEntry) => {
    setData(prev => {
      const newEntries = [...prev.entries]
      newEntries[index] = entry

      // Sort entries chronologically
      newEntries.sort((a, b) =>
        moment(a.finished_at).unix() - moment(b.finished_at).unix()
      )

      // Recalculate BAC
      const entriesWithBAC = calculateBAC(newEntries, prev.gender, prev.weight)

      return { ...prev, entries: entriesWithBAC }
    })
  }, [])

  // Delete an entry
  const deleteEntry = useCallback((index: number) => {
    setData(prev => {
      const newEntries = prev.entries.filter((_, i) => i !== index)
      const entriesWithBAC = calculateBAC(newEntries, prev.gender, prev.weight)
      return { ...prev, entries: entriesWithBAC }
    })
  }, [])

  // Save a drink template
  const saveDrinkTemplate = useCallback((drink: DrinkEntry['drink']) => {
    setData(prev => {
      const existingIndex = prev.drinks.findIndex(d => d.drink === drink.drink)
      let newDrinks: DrinkEntry['drink'][]

      if (existingIndex >= 0) {
        newDrinks = [...prev.drinks]
        newDrinks[existingIndex] = drink
      } else {
        newDrinks = [...prev.drinks, drink]
      }

      return { ...prev, drinks: newDrinks }
    })
  }, [])

  const retrySave = useCallback(() => {
    retryTickRef.current += 1
    setRetryTick(retryTickRef.current)
  }, [])

  const dismissSaveError = useCallback(() => {
    setSaveError(null)
  }, [])

  /**
   * Return the raw password cached in this session (for use by callers
   * that need to send `currentPassword` to a password-gated server
   * endpoint, e.g. PATCH /settings or POST /password to change it).
   * Returns null when the session is locked / no password is set.
   */
  const getCachedPassword = useCallback(() => passwordRef.current, [])

  /**
   * Cache the raw password after a successful unlock for accounts that
   * don't go through supplyPassword / restoreEncryptionKey (i.e.
   * password-protected but NOT E2E encrypted). Callers MUST only invoke
   * this after the password has been verified server-side.
   */
  const cachePassword = useCallback((password: string) => {
    passwordRef.current = password
  }, [])

  return {
    data,
    isLoading,
    isInitialized,
    error,
    loadMode,
    offlineSince,
    saveError,
    needsDecryption,
    isEncrypted: !!encryptionKeyRef.current,
    updateData,
    addEntry,
    updateEntry,
    deleteEntry,
    saveDrinkTemplate,
    supplyPassword,
    clearPassword,
    setPasswordForEncryption,
    restoreEncryptionKey,
    removeEncryption,
    retrySave,
    dismissSaveError,
    getCachedPassword,
    cachePassword,
  }
}
